import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ExternalLink, NotebookPen } from 'lucide-react';
import { format } from 'date-fns';
import type { ProjectMeetingNote } from '../../types';
import { subscribeOpsTaskMeetingNotes } from '../../utils/projectMeetingNotesService';

export interface OpsTaskMeetingNotesProps {
  opsTaskId: string;
  /** Compact strip for the comments popout; default is the side panel layout. */
  variant?: 'panel' | 'popout';
}

function noteHref(note: ProjectMeetingNote): string | null {
  if (!note.projectId || !note.id) return null;
  const q = new URLSearchParams();
  q.set('tab', 'comments');
  q.set('meetingNote', note.id);
  return `/project-hub/${note.projectId}?${q.toString()}`;
}

const OpsTaskMeetingNotes: React.FC<OpsTaskMeetingNotesProps> = ({
  opsTaskId,
  variant = 'panel',
}) => {
  const [notes, setNotes] = useState<ProjectMeetingNote[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opsTaskId) {
      setNotes([]);
      return;
    }
    setError(null);
    return subscribeOpsTaskMeetingNotes(
      opsTaskId,
      setNotes,
      (e) => setError(e.message || 'Could not load meeting notes'),
    );
  }, [opsTaskId]);

  if (error) {
    return (
      <div className={`ops-meeting-notes ops-meeting-notes--${variant}`}>
        <div className="ops-meeting-notes-header">
          <NotebookPen size={14} aria-hidden />
          <span className="ops-detail-label">Meeting notes</span>
        </div>
        <p className="ops-meeting-notes-empty">{error}</p>
      </div>
    );
  }

  if (notes.length === 0) return null;

  return (
    <div className={`ops-meeting-notes ops-meeting-notes--${variant}`}>
      <div className="ops-meeting-notes-header">
        <NotebookPen size={14} aria-hidden />
        <span className="ops-detail-label">Meeting notes</span>
      </div>
      <ul className="ops-meeting-notes-list">
        {notes.map((note) => {
          const href = noteHref(note);
          const id = note.id || note.fathomMeetingId;
          return (
            <li key={id} className="ops-meeting-notes-item">
              <div className="ops-meeting-notes-item-main">
                {href ? (
                  <Link to={href} className="ops-meeting-notes-title" title={note.title}>
                    {note.title}
                    <ExternalLink size={11} aria-hidden />
                  </Link>
                ) : (
                  <span className="ops-meeting-notes-title">{note.title}</span>
                )}
                <div className="ops-meeting-notes-meta">
                  <span className="ops-meeting-notes-date">
                    <Calendar size={11} aria-hidden />
                    {format(note.meetingAt, 'MMM d, yyyy')}
                  </span>
                  {note.audience ? (
                    <span
                      className={`ops-meeting-audience ops-meeting-audience--${note.audience}`}
                      title={note.audienceRationale || undefined}
                    >
                      {note.audience === 'internal' ? 'Internal' : 'External'}
                    </span>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OpsTaskMeetingNotes;
